import Alert from '@mui/material/Alert'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import KNLoader from 'components/KNLoader/KNLoader'
import { useCarriersView } from 'context/CarriersViewContext'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import CarrierFilters from './CarrierFilters'
import CarrierTable from './CarrierTable'
import { CARRIERS_QUERY_KEY, getFilteredCarriers } from './CarrierManager.service'

const CarrierManager = (): ReactElement => {
  const { t } = useTranslation()
  const { filters, page } = useCarriersView()
  const carriersQuery = useQuery({
    queryKey: [CARRIERS_QUERY_KEY, { page, filters }],
    queryFn: () => getFilteredCarriers(page, filters),
    placeholderData: keepPreviousData,
  })

  return (
    <Container maxWidth="xxl">
      {carriersQuery.isPending && (
        <KNLoader>
          <Typography>{t('screens.cs.carrier_manager.loading')}</Typography>
        </KNLoader>
      )}
      {carriersQuery.isError && (
        <Alert severity="error">{getRequestErrorMessage(carriersQuery.error)}</Alert>
      )}
      {carriersQuery.isSuccess && (
        <Paper sx={{ padding: 2 }}>
          <Stack direction="column" spacing={1} mb={1}>
            <CarrierFilters />
            <Typography variant="h6">
              {t('screens.cs.carrier_manager.carriers_count', { count: carriersQuery.data?.totalElements })}
            </Typography>
          </Stack>
          <CarrierTable
            carriers={carriersQuery.data?.content ?? []}
            pageCount={carriersQuery.data?.totalPages}
          />
        </Paper>
      )}
    </Container>
  )
}

export default CarrierManager
